<template>
  <div class="tl mb">
    <Breadcrumbs main="Трансляция MLBB" title="Судьи"/>

    <div class="container-fluid tl-container mb-stack">
      <section class="mb-panel">
        <header class="mb-panel__head">
          <h2>Добавить судью</h2>
          <span class="mb-muted mb-small">По игровому ID судьи бэк ищет матчи в Moonton. Неактивный судья не опрашивается.</span>
        </header>
        <form class="mb-panel__body mb-judges__form" @submit.prevent="add">
          <div class="mb-field mb-judges__id">
            <label for="mb-judge-new-id">Игровой ID <span class="mb-judges__req">*</span></label>
            <input id="mb-judge-new-id" v-model.trim="form.judge_id" class="mb-input mb-mono" inputmode="numeric" placeholder="38332">
          </div>
          <div class="mb-field mb-judges__name">
            <label for="mb-judge-new-name">Название</label>
            <input id="mb-judge-new-name" v-model.trim="form.name" class="mb-input" maxlength="255" placeholder="Stream referee 1">
          </div>
          <label class="mb-check">
            <input v-model="form.is_active" type="checkbox">
            Активен
          </label>
          <button type="submit" class="mb-btn" :disabled="saving">
            <i class="fa fa-plus"></i> Добавить
          </button>
          <span v-if="formError" class="mb-error-text">{{ formError }}</span>
        </form>
      </section>

      <section class="mb-panel">
        <header class="mb-panel__head">
          <h2>Справочник судей</h2>
          <span class="mb-muted mb-small">{{ judges.length }}</span>
          <span class="mb-spacer"></span>
          <button type="button" class="mb-btn mb-btn--ghost mb-btn--sm" :disabled="loading" @click="load">
            <i class="fa fa-refresh"></i> Обновить
          </button>
        </header>

        <div v-if="loading && !judges.length" class="mb-empty"><span class="mb-loading">Загружаем</span></div>
        <div v-else-if="loadError" class="mb-empty">
          <b>Не удалось загрузить справочник</b>
          <button type="button" class="mb-link" @click="load">повторить</button>
        </div>
        <div v-else-if="!judges.length" class="mb-empty">
          <b>Судей пока нет</b>
          Добавьте игровой ID судейского аккаунта, который заходит в комнаты стримовых матчей.
        </div>

        <div v-else class="mb-table-wrap">
          <table class="mb-table">
            <thead>
            <tr>
              <th>Игровой ID</th>
              <th>Название</th>
              <th>Активен</th>
              <th>Последний найденный матч</th>
              <th>Добавлен</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="j in judges" :key="j.id">
              <td class="mb-mono">{{ j.judge_id }}</td>
              <td class="mb-judges__cell-name"><span class="mb-ellipsis" :title="j.name">{{ j.name || '—' }}</span></td>
              <td>
                <b-form-checkbox :key="j.id + '-' + revertKey"
                                 :checked="j.is_active"
                                 switch
                                 :disabled="busyId === j.id"
                                 @change="toggle(j, $event)">
                  {{ j.is_active ? 'да' : 'нет' }}
                </b-form-checkbox>
              </td>
              <td class="mb-small">{{ j.last_discovered_at ? timeAgo(j.last_discovered_at, now) : 'ещё не было' }}</td>
              <td class="mb-small">{{ formatDateTime(j.created_at) }}</td>
              <td class="mb-nowrap">
                <router-link class="mb-link"
                             :to="{name: 'mlbb_broadcast', query: {judge: String(j.judge_id), tab: 'matches'}}">
                  матчи
                </router-link>
                <button type="button"
                        class="mb-btn mb-btn--ghost mb-btn--sm mb-judges__del"
                        :disabled="busyId === j.id"
                        title="Удалить"
                        @click="remove(j)">
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import api from '@/api/api'
import {formatDateTime, parseJudgeId, timeAgo} from '@/common/mlbbBroadcast'

const emptyForm = () => ({judge_id: '', name: '', is_active: true})

export default {
  name: 'MlbbJudgesPage',
  data() {
    return {
      judges: [],
      loading: false,
      loadError: false,
      form: emptyForm(),
      formError: '',
      saving: false,
      busyId: null,
      revertKey: 0,
      now: Date.now(),
    }
  },
  created() {
    this.load()
  },
  methods: {
    formatDateTime,
    timeAgo,

    async load() {
      this.loading = true
      try {
        const res = await api.mlbbEsports.judges()
        this.judges = res?.data?.data || []
        this.loadError = false
        this.now = Date.now()
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },

    async add() {
      const judgeId = parseJudgeId(this.form.judge_id)
      if (!judgeId) {
        this.formError = 'Игровой ID — только цифры'
        return
      }
      if (this.judges.some((j) => j.judge_id === judgeId)) {
        this.formError = 'Такой ID уже есть в справочнике'
        return
      }
      this.formError = ''
      this.saving = true
      try {
        await api.mlbbEsports.storeJudge({
          judge_id: judgeId,
          name: this.form.name || `Судья ${judgeId}`,
          is_active: this.form.is_active,
        })
        this.form = emptyForm()
        await this.load()
      } catch (e) {
        // 422 (дубль) и прочее покажет интерсептор
      } finally {
        this.saving = false
      }
    },

    async toggle(judge, isActive) {
      this.busyId = judge.id
      try {
        const res = await api.mlbbEsports.updateJudge(judge.id, {is_active: isActive})
        const updated = res?.data?.data
        this.judges = this.judges.map((j) => (j.id === judge.id ? {...j, ...(updated || {is_active: isActive})} : j))
      } catch (e) {
        // переключатель уже сдвинулся визуально — пересоздаём его из исходного значения
        this.revertKey += 1
      } finally {
        this.busyId = null
      }
    },

    async remove(judge) {
      const ok = await this.$bvModal.msgBoxConfirm(
          `Удалить судью ${judge.judge_id}${judge.name ? ` (${judge.name})` : ''}? Его матчи перестанут искаться.`,
          {title: 'Удалить судью', okTitle: 'Удалить', cancelTitle: 'Отмена', okVariant: 'danger', centered: true},
      )
      if (!ok) return
      this.busyId = judge.id
      try {
        await api.mlbbEsports.deleteJudge(judge.id)
        this.judges = this.judges.filter((j) => j.id !== judge.id)
      } catch (e) {
        // тост покажет интерсептор
      } finally {
        this.busyId = null
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";
</style>

<style lang="scss">
@import "~@/assets/scss/mlbb-broadcast.scss";

.mb-judges {
  &__form {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;

    .mb-check { height: 34px; }
  }

  &__id { flex: 0 1 180px; }
  &__name { flex: 0 1 320px; }
  &__req { color: var(--tl-danger); }
  &__cell-name { max-width: 280px; }
  &__del { margin-left: 10px; }
}
</style>
